import { FeatureType, GetChallengeResponse, GetPointResponse, PointOrderingOption, SettingStructure } from '../types';
import { LitElement, TemplateResult, css, html, render } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { delay, isDefined, isValidString, undefinedIfEmpty } from '../helpers/utils';
import { getCategoryIcon, getNumberedPointIconColor, getNumberedPointIconShape } from '../helpers/markers';
import { getQRCode, getQueryStringParameters } from '../helpers/urls';

import { Dialog } from '@material/mwc-dialog';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { areLocationsSame } from '../helpers/filters';
import featuresInfo from '../helpers/state/features-info';
import format from 'date-fns/format';
import { getMainPage } from '../helpers/page-name';
import { ifDefined } from 'lit/directives/if-defined';
import { makeColorsIntoNumberSvg } from '../helpers/colors';
import { openChatWindow } from '../helpers/intercom';
import { parseDescriptionText } from '../helpers/embeds';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';

@customElement('printable-page')
export class PrintablePage extends LitElement {
    amplitude_callsite: string = 'Printable Map';

    @query('#upgradeDialogComponent')
    upgradeDialogComponent!: Dialog;

    @state()
    isPublicPrint = false;

    @state()
    numColumns = 3;

    @state()
    qrCodeLink: string | undefined;

    @state()
    showSettings = true;

    @state()
    printSettings: SettingStructure[] = [];

    @state()
    pageSetupSettings: SettingStructure[] = [];

    @state()
    mapSettings: SettingStructure[] = [];

    @state()
    displaySettings: SettingStructure[] = [];

    @state()
    listSettings: SettingStructure[] = [];

    @state()
    showMapPage = true;

    @state()
    showLegend = true;

    @state()
    showQRCode = true;

    @state()
    showHeader = true;

    @state()
    legendBackgroundColor = '#ffffff';

    @state()
    legendTextColor = '#000000';

    @state()
    legendPosition = 'bottom: 0; right: 0; max-width: 40%;';

    @state()
    showListOfPoints = true;

    @state()
    showNumberIcon = true;

    @state()
    showCategoryIcon = true;

    @state()
    showCustomIcon = true;

    @state()
    showTitle = true;

    @state()
    showAddress = true;

    @state()
    showDescription = true;

    @state()
    showDateTime = true;

    @state()
    showSpecialOffer = true;

    @state()
    showTags = true;

    @state()
    showMoreInfo = false;

    @state()
    showCategoryHeaders = false;

    @state()
    categoryHeaderStyle: 'plain' | 'block' = 'plain';

    @state()
    pageHeight = 11;

    @state()
    pageWidth = 8.5;

    @state()
    iconSize = 24;

    @state()
    unit = 'in';

    @state()
    pagePadding = 0.35;

    @state()
    pageSize = 'letter_portrait';

    @state()
    pointOrdering: PointOrderingOption[] = [PointOrderingOption.Number, PointOrderingOption.Category, PointOrderingOption.Alphabetical];

    @state()
    showChecklist = false;

    @state()
    checklistStyle: 'checklist' | 'bingo' = 'checklist';

    @state()
    checklistSubtitle = 'Scan the QR code to register and start checking in!';

    @state()
    selectedChallengeId: string | undefined;

    @state()
    checklistQrCodeDataUrl: string | undefined;

    private bingoCells: Array<{ name: string; icon?: string; isFree: boolean }> = [];
    private bingoGridSize = 5;

    private readonly checklistSetting: SettingStructure = {
        fieldType: 'boolean',
        inputType: 'toggle',
        fieldName: 'show_checklist',
        title: 'Show Checklist Page',
        description: 'Add a printable checklist or bingo card for a challenge with a registration QR code',
        initialValueBoolean: false,
        isTopic: false,
        flippedLanguage: false,
        hasEntitlement: featuresInfo.printableMapEnabled,
        featureType: FeatureType.PrintableMap,
    };

    private readonly showMapPageSetting: SettingStructure = {
        fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_map_page', title: 'Map Page',
        description: 'Include the map page with legend and QR code', initialValueBoolean: true,
        isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap,
    };

    private readonly showListSetting: SettingStructure = {
        fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_list_of_points', title: 'List of Points',
        description: 'Include detailed list of all points', initialValueBoolean: true,
        isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap,
    };

    private readonly checklistSubtitleSetting: SettingStructure = {
        fieldType: 'string',
        inputType: 'inputShort',
        fieldName: 'checklist_subtitle',
        title: 'Subtitle',
        description: 'Custom subtitle shown in the header',
        initialValueString: 'Scan the QR code to register and start checking in!',
        featureType: FeatureType.PrintableMap,
    };

    private readonly checklistStyleSetting: SettingStructure = {
        fieldType: 'string',
        inputType: 'dropdown',
        fieldName: 'checklist_style',
        title: 'Style',
        description: 'Checklist shows a list with checkboxes. Bingo creates a grid card with free spaces.',
        initialValueString: 'checklist',
        dropdownOptions: [
            { value: 'checklist', label: 'Checklist' },
            { value: 'bingo', label: 'Bingo Card' },
        ],
        featureType: FeatureType.PrintableMap,
    };

    private readonly pageSizes: Array<{ value: string; label: string; width: number; height: number }> = [
        { value: 'letter_portrait', label: 'Letter Portrait (8.5 × 11)', width: 8.5, height: 11 },
        { value: 'letter_landscape', label: 'Letter Landscape (11 × 8.5)', width: 11, height: 8.5 },
        { value: 'legal_portrait', label: 'Legal Portrait (8.5 × 14)', width: 8.5, height: 14 },
        { value: 'legal_landscape', label: 'Legal Landscape (14 × 8.5)', width: 14, height: 8.5 },
        { value: 'tabloid', label: 'Tabloid (11 × 17)', width: 11, height: 17 },
        { value: 'ledger', label: 'Ledger (17 × 11)', width: 17, height: 11 },
        { value: 'half_letter', label: 'Half Letter (5.5 × 8.5)', width: 5.5, height: 8.5 },
        { value: 'half_tabloid', label: 'Half Tabloid (5.5 × 11)', width: 5.5, height: 11 },
        { value: 'postcard', label: 'Postcard (6 × 4)', width: 6, height: 4 },
        { value: 'business_card', label: 'Business Card (3.5 × 2)', width: 3.5, height: 2 },
        { value: 'custom', label: 'Custom', width: 0, height: 0 },
    ];

    private applyPageSize(width: number, height: number) {
        const root = document.documentElement;
        this.pageWidth = width;
        this.pageHeight = height;
        root.style.setProperty('--page-width', `${width}${this.unit}`);
        root.style.setProperty('--page-height', `${height}${this.unit}`);
        root.style.setProperty('--page-scale', `${this.updatedScale()}`);
        root.style.setProperty('--column-width', `${width / this.numColumns}${this.unit}`);
        this.requestUpdate();
    }

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated(e: any) {
        // super.firstUpdated(e);
        // look in url for public print
        const queryString = getQueryStringParameters();

        const isPublicPrint =
            queryString.get('isPublicPrint') === 'true' || queryString.get('isPublicPrint') === 'True';
        await this.startup();
        this.isPublicPrint = isPublicPrint && !topicData.isWriter;
        if (this.isPublicPrint) {
            this.showSettings = false;
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.print();
        }
    }

    private async startup() {

        const queryString = getQueryStringParameters();

        const topic_id = queryString.get('topic_id') ?? undefined;
        const topic_key = queryString.get('topic_key') ?? undefined;
        let local_storage_password: string | undefined;
        if (topic_id) {
            local_storage_password = undefinedIfEmpty(localStorage.getItem(`${topic_id ?? ''}_password`)) ?? topic_key;
        }

        await topicData.instantiateTopicData(
            this.amplitude_callsite,
            accountInfo.isAuthenticated,
            false,
            false,
            topic_id,
            local_storage_password,
            this.isPublicPrint
        );

        pointCache.pointOrdering = topicData.topic?.point_sort_order ?? this.pointOrdering;

        // Get the topic and block before getting the points.
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (isDefined(topicData.topic)) {
                await pointCache.instantiatePointCache(
                    topicData.topic,
                    topicData.topicKey,
                    false,
                    false,
                );
            }
        }, 'An error has occurred.  Context: could not get the topic from the server.');

        if (topicData.topic?.standalone_map_link === undefined) {
            return;
        }

        this.qrCodeLink = await getQRCode(
            this.amplitude_callsite,
            topicData.topic.cc_read_link ?? ''
        );

        // ── Page Setup ──
        this.pageSetupSettings = [
            { fieldType: 'string', inputType: 'dropdown', fieldName: 'page_size', title: 'Page Size', description: 'Choose a standard page size or select Custom', initialValueString: 'letter_portrait', dropdownOptions: this.pageSizes.map((s) => ({ value: s.value, label: s.label })), featureType: FeatureType.PrintableMap },
            { fieldType: 'string', inputType: 'dropdown', fieldName: 'unit', title: 'Unit', description: 'Unit of measurement', initialValueString: 'in', dropdownOptions: [{ value: 'in', label: 'Inches' }, { value: 'cm', label: 'Centimeters' }], featureType: FeatureType.PrintableMap },
            { fieldType: 'number', inputType: 'inputShort', fieldName: 'aspect_ratio_width', title: 'Width', description: 'Page width', numberMax: 300, numberMin: 1, initialValueNumber: this.pageWidth, widthPercentage: 49, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'number', inputType: 'inputShort', fieldName: 'aspect_ratio_height', title: 'Height', description: 'Page height', numberMax: 300, numberMin: 1, initialValueNumber: this.pageHeight, widthPercentage: 49, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'number', inputType: 'slider', fieldName: 'page_padding', title: 'Padding', description: 'Space around the edge of each page (0–1 inches)', initialValueNumber: this.pagePadding * 100, numberMax: 100, numberMin: 0, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap, sliderStep: 1 },
        ];

        // ── Map Page settings ──
        this.mapSettings = [
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_header', title: 'Show Header', description: 'Show a header on the map page', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_qr_code', title: 'Show QR Code', description: 'Show a QR code on the map page', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_legend', title: 'Show Legend', description: 'Show a category legend on the map page', initialValueBoolean: true, subfields: ['legend_position', 'legend_background_color', 'legend_text_color'], isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'string', inputType: 'dropdown', fieldName: 'legend_position', title: 'Legend Position', description: 'Position of the legend', initialValueString: 'bottom right', dropdownOptions: [{ value: 'bottom right', label: 'Bottom Right' }, { value: 'bottom left', label: 'Bottom Left' }, { value: 'top right', label: 'Top Right' }, { value: 'top left', label: 'Top Left' }, { value: 'top', label: 'Top' }, { value: 'bottom', label: 'Bottom' }, { value: 'left', label: 'Left' }, { value: 'right', label: 'Right' }], isTopic: false, isSubfield: true, isHidden: true, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'string', inputType: 'color', fieldName: 'legend_background_color', title: 'Legend Background Color', description: 'Background color of the legend', initialValueString: '#ffffff', isTopic: false, isSubfield: true, isHidden: true, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'string', inputType: 'color', fieldName: 'legend_text_color', title: 'Legend Text Color', description: 'Text color of the legend', initialValueString: '#000000', isTopic: false, isSubfield: true, isHidden: true, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
        ];

        // ── Shared display settings (affect list + checklist) ──
        this.displaySettings = [
            { fieldType: 'number', inputType: 'inputShort', fieldName: 'num_columns', title: 'Number of Columns', description: 'Number of columns for the list and checklist pages', initialValueNumber: 3, numberMax: 10, numberMin: 1, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'number', inputType: 'slider', fieldName: 'text_scale', title: 'Text Scale', description: 'Adjust text size for the list and checklist pages (1-40)', initialValueNumber: 12, numberMax: 40, numberMin: 1, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap, sliderStep: 1 },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_number_icon', title: 'Show Number Icon', description: 'Show numbered icons', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_category_icon', title: 'Show Category Icon', description: 'Show category icons', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_custom_icon', title: 'Show Custom Icon', description: 'Show custom icons if any exist', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
        ];

        // ── List of Points only settings ──
        this.listSettings = [
            { fieldType: 'string', inputType: 'dropdown', fieldName: 'point_ordering', title: 'Point Ordering', description: 'Sort order for points', initialValueString: this.pointOrdering[0], dropdownOptions: Object.values(PointOrderingOption).map((option) => ({ label: option === PointOrderingOption.Start ? 'Chronological' : option, value: option })), hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_category_headers', title: 'Show Category Headers', description: 'Show category name at the beginning of each section', initialValueBoolean: false, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'string', inputType: 'dropdown', fieldName: 'category_header_style', title: 'Header Style', description: 'Plain shows text only. Block uses map header colors.', initialValueString: 'plain', dropdownOptions: [{ value: 'plain', label: 'Plain' }, { value: 'block', label: 'Block' }], hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_title', title: 'Show Title', description: 'Show point title', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_address', title: 'Show Address', description: 'Show point address', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_description', title: 'Show Description', description: 'Show point description', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_date_time', title: 'Show Date and Time', description: 'Show event date/time', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_special_offer', title: 'Show Special Offer', description: 'Show special offers', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_tags', title: 'Show Tags', description: 'Show tags', initialValueBoolean: true, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
            { fieldType: 'boolean', inputType: 'toggle', fieldName: 'show_more_info', title: 'Show More Info Link', description: 'Show a link for more info', initialValueBoolean: false, isTopic: false, flippedLanguage: false, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap },
        ];

        // Keep printSettings for backwards compat with toggleSubfield
        this.printSettings = [...this.pageSetupSettings, ...this.mapSettings, ...this.displaySettings, ...this.listSettings];

        // If the topic has challenges, auto-select the first one
        if (topicData.allChallenges.length > 0) {
            this.selectedChallengeId = topicData.allChallenges[0]._id.$oid;
            await this.generateChecklistQrCode(topicData.allChallenges[0]);
        }

        // Apply URL query parameters for pre-configured views
        // e.g. #printable-page?topic_id=X&checklist_only=true&checklist_style=bingo&challenge_id=Y&subtitle=Hello
        const checklistOnly = queryString.get('checklist_only') === 'true';
        const qsChecklistStyle = queryString.get('checklist_style');
        const qsChallengeId = queryString.get('challenge_id');
        const qsSubtitle = queryString.get('subtitle');

        if (checklistOnly && topicData.allChallenges.length > 0) {
            this.showMapPage = false;
            this.showListOfPoints = false;
            this.showChecklist = true;
            this.showSettings = false;
        }
        if (qsChecklistStyle === 'bingo' || qsChecklistStyle === 'checklist') {
            this.checklistStyle = qsChecklistStyle;
            this.showChecklist = true;
        }
        if (qsChallengeId) {
            const match = topicData.allChallenges.find((c) => c._id.$oid === qsChallengeId);
            if (match) {
                this.selectedChallengeId = qsChallengeId;
                await this.generateChecklistQrCode(match);
            }
        }
        if (qsSubtitle !== null && qsSubtitle !== undefined) {
            this.checklistSubtitle = qsSubtitle;
        }
        if (this.checklistStyle === 'bingo') {
            this.bingoCells = [];
        }

        // set initial values for css variables
        const root = document.documentElement;
        root.style.setProperty('--page-width', `${this.pageWidth}in`);
        root.style.setProperty('--page-height', `${this.pageHeight}in`);
        root.style.setProperty('--page-padding', `${this.pagePadding}in`);
        root.style.setProperty('--page-scale', `${this.updatedScale()}`);
        root.style.setProperty('--column-width', `${this.pageWidth / this.numColumns}in`);
        root.style.setProperty('--legend-background-color', this.legendBackgroundColor);
        root.style.setProperty('--legend-text-color', this.legendTextColor);
        root.style.setProperty('--text-scale', '1');

        document.documentElement.style.setProperty('--custom-font', topicData.topic?.custom_font ?? 'Montserrat');
        this.style.setProperty('font-family', topicData.topic?.custom_font ?? 'Montserrat');

        this.requestUpdate();
        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private toggleSubfield(e: CustomEvent, settingList: SettingStructure[]) {
        // find title in settingsList as either fieldnametoggle or fieldname textand set isHidden to false if it is true and true if it is false
        const fieldTitle = e.detail.fieldTitle;
        const setting = settingList.find((setting) => setting.fieldName === fieldTitle);

        if (isDefined(setting)) {
            if (e.detail.hidden === false) {
                setting.isHidden = false;
            } else {
            setting.isHidden = !(setting.isHidden ?? false);
            }
        }
        // update the new isHidden value in this.settingsList
        settingList = [...settingList];
    }

    private updatedScale(): number {
        return Math.min(6 / this.pageWidth, 8 / this.pageHeight);
    }

    private openUpgradeDialog() {
        this.upgradeDialogComponent.open = true;
    }


    private getLegendPosition(position: string): string {
        if (position === 'bottom right') {
            return 'bottom: 0; right: 0; max-width: 40%; display: flex; flex-direction: column;';
        } else if (position === 'bottom left') {
            return 'bottom: 0; left: 0; max-width: 40%; display: flex; flex-direction: column;';
        } else if (position === 'top right') {
            return 'top: 0; right: 0; max-width: 40%; display: flex; flex-direction: column;';
        } else if (position === 'top left') {
            return 'top: 0; left: 0; max-width: 40%; display: flex; flex-direction: column;';
        } else if (position === 'top') {
            return 'top: 0; display: inline-flex; width: auto; column-gap: 1rem; flex-wrap: wrap;';
        } else if (position === 'bottom') {
            return 'bottom: 0; display: inline-flex; width: auto; column-gap: 1rem; flex-wrap: wrap;';
        } else if (position === 'left') {
            return 'left: 0; height: 100%; display: flex; flex-direction: column; row-gap: 0.5rem;';
        } else if (position === 'right') {
            return 'right: 0; height: 100%; display: flex; flex-direction: column; row-gap: 0.5rem;';
        } else {
            return 'bottom: 0; right: 0; max-width: 40%;';
        }
    }

    private eventString(point: GetPointResponse): string | undefined {
        if (!point.start || !point.end) return undefined;

        const timeOnly = topicData.topic?.has_time_only === true;
        const dateOnly = topicData.topic?.has_date_only === true;

        const start = new Date(point.start);
        const end = new Date(point.end);

        if (timeOnly) {
            return `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`;
        }

        const startDate = format(start, 'MMM dd, yyyy');
        const endDate = format(end, 'MMM dd, yyyy');

        if (dateOnly) {
            return `${startDate} - ${endDate}`;
        }

        const timezoneString = new Date().toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];
        return `${startDate} ${format(start, 'h:mm a')} - ${endDate} ${format(end, 'h:mm a')} (${timezoneString})`;
    }

    private hasChange(e: CustomEvent) {
        const newUpdatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        const root = document.documentElement;

        // send to parent in mobile
        switch (fieldToUpdate) {
            case 'show_map_page':
                this.showMapPage = newUpdatedValue;
                break;
            case 'show_legend':
                this.showLegend = newUpdatedValue;
                break;
            case 'show_qr_code':
                this.showQRCode = newUpdatedValue;
                break;
            case 'show_header':
                this.showHeader = newUpdatedValue;
                break;
            case 'show_list_of_points':
                this.showListOfPoints = newUpdatedValue;
                break;
            case 'show_title':
                this.showTitle = newUpdatedValue;
                break;
            case 'show_address':
                this.showAddress = newUpdatedValue;
                break;
            case 'show_description':
                this.showDescription = newUpdatedValue;
                break;
            case 'show_date_time':
                this.showDateTime = newUpdatedValue;
                break;
            case 'show_special_offer':
                this.showSpecialOffer = newUpdatedValue;
                break;
            case 'page_size': {
                this.pageSize = newUpdatedValue;
                const sizeOption = this.pageSizes.find((s) => s.value === newUpdatedValue);
                if (sizeOption && sizeOption.value !== 'custom') {
                    this.applyPageSize(sizeOption.width, sizeOption.height);
                }
                break;
            }
            case 'unit':
                this.unit = newUpdatedValue;
                this.applyPageSize(this.pageWidth, this.pageHeight);
                break;
            case 'aspect_ratio_height':
                this.applyPageSize(this.pageWidth, newUpdatedValue);
                break;
            case 'aspect_ratio_width':
                this.applyPageSize(newUpdatedValue, this.pageHeight);
                break;
            case 'page_padding':
                this.pagePadding = newUpdatedValue / 100;
                document.documentElement.style.setProperty('--page-padding', `${this.pagePadding}in`);
                this.requestUpdate();
                break;
            case 'num_columns':
                this.numColumns = newUpdatedValue;
                root.style.setProperty('--column-width', `${this.pageWidth / newUpdatedValue}in`);
                this.requestUpdate();
                break;
            case 'show_tags':
                this.showTags = newUpdatedValue;
                break;
            case 'show_more_info':
                this.showMoreInfo = newUpdatedValue;
                break;
            case 'text_scale':
                root.style.setProperty('--text-scale', (newUpdatedValue/12).toString());
                this.iconSize = 24 * newUpdatedValue / 16;
                break;
            case 'point_ordering':
                this.pointOrdering = [newUpdatedValue];
                pointCache.pointOrdering = [newUpdatedValue];
                if (newUpdatedValue !== PointOrderingOption.Category) {
                    this.showCategoryHeaders = false;
                }
                break;
            case 'legend_background_color':
                this.legendBackgroundColor = newUpdatedValue;
                root.style.setProperty('--legend-background-color', newUpdatedValue);
                break;
            case 'legend_text_color':
                this.legendTextColor = newUpdatedValue;
                root.style.setProperty('--legend-text-color', newUpdatedValue);
                break;
            case 'legend_position':
                this.legendPosition = this.getLegendPosition(newUpdatedValue);
                break;
            case 'show_number_icon':
                this.showNumberIcon = newUpdatedValue;
                break;
            case 'show_category_icon':
                this.showCategoryIcon = newUpdatedValue;
                break;
            case 'show_custom_icon':
                this.showCustomIcon = newUpdatedValue;
                break;
            case 'show_category_headers':
                this.showCategoryHeaders = newUpdatedValue;
                break;
            case 'show_checklist':
                this.showChecklist = newUpdatedValue;
                break;
            case 'checklist_style':
                this.checklistStyle = newUpdatedValue;
                if (newUpdatedValue === 'bingo') {
                    this.bingoCells = [];
                }
                break;
            case 'checklist_subtitle':
                this.checklistSubtitle = newUpdatedValue;
                break;
            case 'category_header_style':
                this.categoryHeaderStyle = newUpdatedValue;
                break;
            default:
                break;
        }

        this.requestUpdate();
    }

    private async generateChecklistQrCode(challenge: GetChallengeResponse) {
        await topicData.getCommandCenterShareLink();
        let link = topicData.publicShareLink;
        if (!link) {
            return;
        }
        if (topicData.verifiedAlternateHost) {
            link = topicData.swapUrlForOrgUrl(link);
        }
        const registrationLink = `${link}?view=registration&challenge_id=${challenge._id.$oid}`;

        const qr = (window as any).qrcode(0, 'L');
        qr.addData(registrationLink);
        qr.make();
        this.checklistQrCodeDataUrl = qr.createDataURL(8, 32);
    }

    private async onChallengeSelected(e: Event) {
        const select = e.target as HTMLSelectElement;
        this.selectedChallengeId = select.value;
        this.bingoCells = [];
        const challenge = topicData.allChallenges.find((c) => c._id.$oid === this.selectedChallengeId);
        if (challenge) {
            await this.generateChecklistQrCode(challenge);
        }
    }

    private getChecklistPoints(): GetPointResponse[] {
        const challenge = topicData.allChallenges.find((c) => c._id.$oid === this.selectedChallengeId);
        if (!challenge) {
            return [];
        }
        const activeLocations = challenge.locations.filter((loc) => loc.is_active);
        return pointCache.orderedPoints.filter((point) =>
            activeLocations.some((loc) => areLocationsSame(point.location, loc.location))
        );
    }

    private removeChecklistPages() {
        const checklistPages = this.shadowRoot?.querySelectorAll('.rootContainer.checklist-page');
        checklistPages?.forEach((el) => el.remove());
    }

    private renderChecklistPages() {
        const scrollTop = this.saveScrollPosition();
        this.removeChecklistPages();

        const challenge = topicData.allChallenges.find((c) => c._id.$oid === this.selectedChallengeId);
        if (!challenge) {
            this.restoreScrollPosition(scrollTop);
            return;
        }
        const points = this.getChecklistPoints();

        const printArea = this.shadowRoot?.querySelector('.printArea');
        if (!printArea) {
            return;
        }

        const createChecklistPage = (includeHeader: boolean) => {
            const container = document.createElement('div');
            container.classList.add('rootContainer', 'pagebreak', 'checklist-page');
            container.style.height = `${this.pageHeight}${this.unit}`;
            container.style.width = `${this.pageWidth}${this.unit}`;
            container.style.display = 'flex';
            container.style.flexDirection = 'column';
            container.style.paddingBottom = `${this.pagePadding}in`;

            let headerHeight = 0;
            if (includeHeader) {
                const headerColor = topicData.topic?.header_color ?? '#333333';
                const headerTextColor = topicData.topic?.header_text_color ?? '#ffffff';
                const logoUrl = topicData.topic?.header_logo;

                const header = document.createElement('div');
                header.classList.add('checklist-header');
                header.style.backgroundColor = headerColor;
                header.style.color = headerTextColor;

                const titleArea = document.createElement('div');
                titleArea.classList.add('checklist-title-area');
                const title = document.createElement('h2');
                title.classList.add('checklist-title');
                title.textContent = challenge.name;
                titleArea.appendChild(title);
                if (this.checklistSubtitle) {
                    const subtitle = document.createElement('p');
                    subtitle.classList.add('checklist-subtitle');
                    subtitle.textContent = this.checklistSubtitle;
                    titleArea.appendChild(subtitle);
                }
                header.appendChild(titleArea);

                if (logoUrl) {
                    const logo = document.createElement('img');
                    logo.src = logoUrl;
                    logo.classList.add('checklist-logo');
                    logo.alt = 'Logo';
                    header.appendChild(logo);
                }

                if (this.checklistQrCodeDataUrl) {
                    const qr = document.createElement('img');
                    qr.src = this.checklistQrCodeDataUrl;
                    qr.classList.add('checklist-qr');
                    qr.alt = 'Registration QR Code';
                    header.appendChild(qr);
                }

                container.appendChild(header);
                printArea.appendChild(container);
                headerHeight = header.offsetHeight;
            } else {
                printArea.appendChild(container);
            }

            const columnsWrapper = document.createElement('div');
            columnsWrapper.style.display = 'flex';
            columnsWrapper.style.flexDirection = 'row';
            columnsWrapper.style.flex = '1';
            container.appendChild(columnsWrapper);

            const columns: HTMLElement[] = [];
            for (let i = 0; i < this.numColumns; i++) {
                const col = document.createElement('div');
                col.id = 'points';
                columnsWrapper.appendChild(col);
                columns.push(col);
            }

            return { container, columns, headerHeight };
        };

        let { container: currentContainer, columns, headerHeight } = createChecklistPage(true);
        const pageHeightPx = currentContainer.clientHeight;
        const defaultColumnHeight = pageHeightPx - (6.5 * 16);
        const remainingColumnHeights: number[] = [];
        for (let i = 0; i < this.numColumns; i++) {
            remainingColumnHeights.push(defaultColumnHeight - headerHeight);
        }
        let currentColumnIndex = 0;

        const appendItem = (element: HTMLElement) => {
            columns[currentColumnIndex].appendChild(element);
            const elementHeight = element.clientHeight;

            if (elementHeight <= remainingColumnHeights[currentColumnIndex]) {
                remainingColumnHeights[currentColumnIndex] -= elementHeight;
            } else {
                currentColumnIndex++;
                if (currentColumnIndex >= this.numColumns) {
                    ({ container: currentContainer, columns, headerHeight } = createChecklistPage(false));
                    currentColumnIndex = 0;
                    remainingColumnHeights.fill(defaultColumnHeight);
                }
                columns[currentColumnIndex].appendChild(element);
                remainingColumnHeights[currentColumnIndex] -= element.clientHeight;
            }
        };

        points.forEach((point) => {
            const category = topicData.categories.find((c) => c._id?.$oid === point?.topic_category_id.$oid);
            const name = point.name ?? category?.name ?? '';

            const item = document.createElement('div');
            item.classList.add('checklist-item');

            const checkbox = document.createElement('div');
            checkbox.classList.add('checklist-checkbox');
            item.appendChild(checkbox);

            const content = document.createElement('div');
            content.classList.add('checklist-item-content');

            if (this.showCategoryIcon && category) {
                const icon = document.createElement('img');
                icon.src = category.icon;
                icon.width = this.iconSize;
                icon.height = this.iconSize;
                content.appendChild(icon);
            }

            const nameSpan = document.createElement('span');
            nameSpan.classList.add('checklist-item-name');
            nameSpan.textContent = name;
            content.appendChild(nameSpan);

            item.appendChild(content);
            appendItem(item);
        });
        this.restoreScrollPosition(scrollTop);
    }

    private getBingoGridSize(count: number): number {
        if (count <= 9) return 3;
        if (count <= 16) return 4;
        if (count <= 25) return 5;
        if (count <= 36) return 6;
        if (count <= 49) return 7;
        return 8;
    }

    /** Get grid indices along both diagonals, sorted from center outward */
    private diagonalIndices(gridSize: number): number[] {
        const center = (gridSize - 1) / 2;
        const indices: number[] = [];
        for (let r = 0; r < gridSize; r++) {
            for (let c = 0; c < gridSize; c++) {
                if (r === c || r + c === gridSize - 1) {
                    indices.push(r * gridSize + c);
                }
            }
        }
        // Sort by distance from center so we fill from the middle of the X out
        indices.sort((a, b) => {
            const ar = Math.floor(a / gridSize);
            const ac = a % gridSize;
            const br = Math.floor(b / gridSize);
            const bc = b % gridSize;
            const distA = Math.abs(ar - center) + Math.abs(ac - center);
            const distB = Math.abs(br - center) + Math.abs(bc - center);
            return distA - distB;
        });
        return indices;
    }

    private generateBingoCells() {
        const points = this.getChecklistPoints();
        const gridSize = this.getBingoGridSize(points.length);
        const totalCells = gridSize * gridSize;
        const freeCount = totalCells - points.length;

        // Mark FREE positions — prefer diagonals, then fill center-out
        const freePositions = new Set<number>();
        if (freeCount > 0) {
            const diags = this.diagonalIndices(gridSize);
            for (let i = 0; i < freeCount && i < diags.length; i++) {
                freePositions.add(diags[i]);
            }
            // If more FREE cells needed than diagonal slots, fill remaining center-out
            if (freePositions.size < freeCount) {
                const center = (gridSize - 1) / 2;
                const remaining = Array.from({ length: totalCells }, (_, i) => i)
                    .filter((i) => !freePositions.has(i))
                    .sort((a, b) => {
                        const distA = Math.abs(Math.floor(a / gridSize) - center) + Math.abs((a % gridSize) - center);
                        const distB = Math.abs(Math.floor(b / gridSize) - center) + Math.abs((b % gridSize) - center);
                        return distA - distB;
                    });
                for (const idx of remaining) {
                    if (freePositions.size >= freeCount) break;
                    freePositions.add(idx);
                }
            }
        }

        // Shuffle the location points
        const shuffled = [...points].sort(() => Math.random() - 0.5);

        // Build cells — FREE on marked positions, locations elsewhere, fallback to FREE
        const cells: Array<{ name: string; icon?: string; isFree: boolean }> = [];
        let locIndex = 0;
        for (let i = 0; i < totalCells; i++) {
            if (freePositions.has(i)) {
                cells.push({ name: 'FREE', isFree: true });
            } else if (locIndex < shuffled.length) {
                const point = shuffled[locIndex];
                const category = topicData.categories.find((c) => c._id?.$oid === point?.topic_category_id.$oid);
                cells.push({ name: point?.name ?? category?.name ?? '', icon: category?.icon, isFree: false });
                locIndex++;
            } else {
                cells.push({ name: 'FREE', isFree: true });
            }
        }

        this.bingoGridSize = gridSize;
        this.bingoCells = cells;
    }

    private removeBingoPage() {
        const bingoPages = this.shadowRoot?.querySelectorAll('.rootContainer.bingo-page');
        bingoPages?.forEach((el) => el.remove());
    }

    private renderBingoPage() {
        const scrollTop = this.saveScrollPosition();
        this.removeBingoPage();

        if (this.bingoCells.length === 0) {
            this.generateBingoCells();
        }
        if (this.bingoCells.length === 0) {
            this.restoreScrollPosition(scrollTop);
            return;
        }

        const challenge = topicData.allChallenges.find((c) => c._id.$oid === this.selectedChallengeId);
        if (!challenge) {
            this.restoreScrollPosition(scrollTop);
            return;
        }

        const printArea = this.shadowRoot?.querySelector('.printArea');
        if (!printArea) {
            this.restoreScrollPosition(scrollTop);
            return;
        }

        const headerColor = topicData.topic?.header_color ?? '#333333';
        const headerTextColor = topicData.topic?.header_text_color ?? '#ffffff';
        const logoUrl = topicData.topic?.header_logo;

        const container = document.createElement('div');
        container.classList.add('rootContainer', 'pagebreak', 'bingo-page');
        container.style.height = `${this.pageHeight}${this.unit}`;
        container.style.width = `${this.pageWidth}${this.unit}`;
        container.style.display = 'flex';
        container.style.flexDirection = 'column';

        // Header
        const header = document.createElement('div');
        header.classList.add('checklist-header');
        header.style.backgroundColor = headerColor;
        header.style.color = headerTextColor;

        const titleArea = document.createElement('div');
        titleArea.classList.add('checklist-title-area');
        const title = document.createElement('h2');
        title.classList.add('checklist-title');
        title.textContent = challenge.name;
        titleArea.appendChild(title);
        if (this.checklistSubtitle) {
            const subtitle = document.createElement('p');
            subtitle.classList.add('checklist-subtitle');
            subtitle.textContent = this.checklistSubtitle;
            titleArea.appendChild(subtitle);
        }
        header.appendChild(titleArea);

        if (logoUrl) {
            const logo = document.createElement('img');
            logo.src = logoUrl;
            logo.classList.add('checklist-logo');
            logo.alt = 'Logo';
            header.appendChild(logo);
        }

        if (this.checklistQrCodeDataUrl) {
            const qr = document.createElement('img');
            qr.src = this.checklistQrCodeDataUrl;
            qr.classList.add('checklist-qr');
            qr.alt = 'Registration QR Code';
            header.appendChild(qr);
        }

        container.appendChild(header);

        // Grid
        const grid = document.createElement('div');
        grid.classList.add('bingo-grid');
        grid.style.gridTemplateColumns = `repeat(${this.bingoGridSize},1fr)`;
        grid.style.gridTemplateRows = `repeat(${this.bingoGridSize},1fr)`;

        this.bingoCells.forEach((cell) => {
            const cellEl = document.createElement('div');
            cellEl.classList.add('bingo-cell');
            if (cell.isFree) {
                cellEl.classList.add('bingo-free');
            }

            if (!cell.isFree && this.showCategoryIcon && cell.icon) {
                const icon = document.createElement('img');
                icon.src = cell.icon;
                icon.width = 20;
                icon.height = 20;
                cellEl.appendChild(icon);
            }

            const nameSpan = document.createElement('span');
            nameSpan.classList.add('bingo-cell-name');
            nameSpan.textContent = cell.name;
            cellEl.appendChild(nameSpan);

            grid.appendChild(cellEl);
        });

        container.appendChild(grid);
        printArea.appendChild(container);
        this.restoreScrollPosition(scrollTop);
    }

    private renderPageSetupSettings() {
        const shared = this.pageSetupSettings
            .filter((s) => s.fieldName === 'page_size' || s.fieldName === 'page_padding')
            .map((setting) => html`
                <setting-component
                    .setting_details=${setting}
                    @open-upgrade-dialog=${this.openUpgradeDialog}
                    @on-change-value=${this.hasChange}
                ></setting-component>`);

        if (this.pageSize !== 'custom') {
            return shared;
        }

        const unitSetting: SettingStructure = {
            fieldType: 'string', inputType: 'dropdown', fieldName: 'unit', title: 'Unit',
            description: 'Unit of measurement', initialValueString: this.unit,
            dropdownOptions: [{ value: 'in', label: 'Inches' }, { value: 'cm', label: 'Centimeters' }],
            featureType: FeatureType.PrintableMap,
        };
        const widthSetting: SettingStructure = {
            fieldType: 'number', inputType: 'inputShort', fieldName: 'aspect_ratio_width', title: 'Width',
            description: 'Page width', numberMax: 300, numberMin: 1, initialValueNumber: this.pageWidth,
            widthPercentage: 49, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap,
        };
        const heightSetting: SettingStructure = {
            fieldType: 'number', inputType: 'inputShort', fieldName: 'aspect_ratio_height', title: 'Height',
            description: 'Page height', numberMax: 300, numberMin: 1, initialValueNumber: this.pageHeight,
            widthPercentage: 49, hasEntitlement: featuresInfo.printableMapEnabled, featureType: FeatureType.PrintableMap,
        };

        return html`
            ${shared}
            <setting-component .setting_details=${unitSetting} @on-change-value=${this.hasChange}></setting-component>
            <setting-component style="width:49%;" .setting_details=${widthSetting} @open-upgrade-dialog=${this.openUpgradeDialog} @on-change-value=${this.hasChange}></setting-component>
            <setting-component style="width:49%;" .setting_details=${heightSetting} @open-upgrade-dialog=${this.openUpgradeDialog} @on-change-value=${this.hasChange}></setting-component>
        `;
    }

    private async print() {
        await this.updateComplete;
        await delay(1000);
        window.print();
    }

    private async copyTextContent() {
        // copy inner text content of #points item to clipboard
        const pointsElement = this.shadowRoot?.querySelector('#points') as HTMLElement;
        const textToCopy = pointsElement?.innerText;
        if (textToCopy) {
            await navigator.clipboard.writeText(textToCopy);
            getMainPage().errorToastElement.showToast('Text content copied to clipboard');
        }
    }

    renderPointArea(point: GetPointResponse): TemplateResult {
        const category = topicData.categories.find((c) => c._id?.$oid === point?.topic_category_id.$oid);
        if (!category) {
            return html``;
        }
        const name = point.name ?? category.name;
        const hasCustomPointIcon = this.showCustomIcon && isDefined(undefinedIfEmpty(point.custom_icon_url));
        return html`
            <div class="point-info">
                <div slot="summary" class="summary">
                    ${topicData.topic?.enable_point_numbering === true && point.number !== undefined && this.showNumberIcon
                        ? html`<img
                            src="${makeColorsIntoNumberSvg(
                                getNumberedPointIconColor(topicData.topic, point.topic_category_id.$oid),
                                point.number,
                                getNumberedPointIconShape(topicData.topic, point.topic_category_id.$oid)
                            )}"
                            style="margin-right: -5px; z-index: 1;"
                            alt=""
                            width="${this.iconSize}"
                            height="${this.iconSize}"
                        />`
                        : ''}
                    ${hasCustomPointIcon ? html` <img id="iconImage" src="${point.custom_icon_url ?? ''}" width="${this.iconSize}" height="${this.iconSize}" />` : ''}
                    ${this.showCategoryIcon && !hasCustomPointIcon ? html` <img id="iconImage" src="${getCategoryIcon(category?._id)}" width="${this.iconSize}" height="${this.iconSize}" />` : ''}
                    ${this.showTitle ? html`<div id="summaryName">${name}</div>` : ''}
                </div>
                ${this.showAddress ? html`<div id="summaryAddress">${point.location.search}</div>` : ''}
                ${this.showDateTime && point.start ? html`<div id="summaryDateTime">${this.eventString(point)}</div>` : ''}
                ${this.showDescription && isDefined(undefinedIfEmpty(point.description)) ? html`<div id="summaryDescription">${unsafeHTML(parseDescriptionText(point.description ?? '', topicData.topic?.open_links_in_same_tab, topicData.topic?.embed_all_description_links, false))}</div>` : ''}
                ${this.showSpecialOffer && point.special_offer?.description ? html`<div id="summaryOffer">${point.special_offer?.description}</div>` : ''}
                ${this.showTags && (point.tag_info?.user_tags.length ?? 0) > 0 ? html`<div id="summaryTags">
                    ${point.tag_info?.user_tags.map((tag) => {
                        return html`
                            <span class="tag">${tag.name}</span>
                        `;
                    })}
                </div>` : ''}
                ${this.showMoreInfo && isDefined(undefinedIfEmpty(point.url)) ? html`<div id="summaryMoreInfo"><a href="${point.url}" target="_blank" rel="noopener noreferrer">Link</a></div>` : ''}
            </div>
        `;
    }

    render() {
        // if no filter show all categories otherwise show only the categories that are in the filter
        const categories = pointCache.categoryFilterList.length > 0 ? topicData.categories.filter((c) => pointCache.categoryFilterList.includes(c.name)) : topicData.categories;
        return html`
            <div class="pageContainer">
                <div class="printArea">
                    ${topicData.isReady && this.showMapPage
                        ? html`
                        <div class="rootContainer" style="height:${this.pageHeight}${this.unit};width:${this.pageWidth}${this.unit}">
                            ${this.showHeader ? html`
                                <map-header> </map-header>`
                            : ''}
                            <div class="flexContainer" id="mainContainer">
                                <div id="map">
                                    <map-component
                                        .isPrintView=${true}
                                        hideCategoriesandTags=${true}>
                                    </map-component>
                                    ${this.showQRCode ? html`
                                        <img src=${ifDefined(this.qrCodeLink)} class="qrCodeImg" alt="qr code" />`
                                    : ''}
                                    ${this.showLegend ? html`
                                        <div id="categories" style="${this.legendPosition}">
                                            ${categories.map(
                                                (c) => html`
                                                <div class="legend-item">
                                                    <img src="${c.icon}" width="24" height="24" />
                                                    <div class="legend-text">${c.name}</div>
                                                </div>`
                                            )}
                                        </div>`
                                    : ''}
                                </div>

                            </div>
                        </div>`
                    : ''}
                ${this.showListOfPoints ? html`
                    ${this.rendermeasurePointsAndAdjustLayout(pointCache.orderedPoints)}
                ` : html`${this.removePointListPages()}`}
                ${this.showChecklist
                    ? this.checklistStyle === 'bingo'
                        ? html`${this.removeChecklistPages()}${this.renderBingoPage()}`
                        : html`${this.removeBingoPage()}${this.renderChecklistPages()}`
                    : html`${this.removeChecklistPages()}${this.removeBingoPage()}`}
                </div>
                ${this.showSettings ? html`
                    <div class="settings">
                        <form-button
                            id="printButton"
                            class="printButtonSticky"
                            size="md"
                            @click=${featuresInfo.printableMapEnabled ? this.print : this.openUpgradeDialog}
                            > ${featuresInfo.printableMapEnabled ? 'Print Now' : 'Upgrade to Print'}
                        </form-button>
                        <div style="flex: 1; overflow-y: auto; overflow-x: hidden; padding-right: 1rem;">

                            <!-- ── Page Setup ── -->
                            <h4 class="section-header">Page Setup</h4>
                            ${this.renderPageSetupSettings()}

                            <!-- ── Pages to Include ── -->
                            <h4 class="section-header">Pages to Include</h4>
                            <setting-component
                                .setting_details=${this.showMapPageSetting}
                                @open-upgrade-dialog=${this.openUpgradeDialog}
                                @on-change-value=${this.hasChange}
                            ></setting-component>
                            <setting-component
                                .setting_details=${this.showListSetting}
                                @open-upgrade-dialog=${this.openUpgradeDialog}
                                @on-change-value=${this.hasChange}
                            ></setting-component>
                            ${topicData.allChallenges.length > 0 ? html`
                                <setting-component
                                    .setting_details=${this.checklistSetting}
                                    @open-upgrade-dialog=${this.openUpgradeDialog}
                                    @on-change-value=${this.hasChange}
                                ></setting-component>
                            ` : ''}

                            ${topicData.categories.length > 1 || topicData.allUserTags.length > 0 ? html`
                                <!-- ── Filters ── -->
                                <h4 class="section-header">Filters</h4>
                                <div style="display:flex;width:100%;margin-bottom:0.5rem;">
                                    <filter-chips .show=${true} @clear-all-filters=${() => pointCache.removeFilter()} style="position:relative;display:flex;align-content:center;justify-content:center;flex-direction:column;max-width:350px;">
                                    </filter-chips>
                                </div>
                            ` : ''}

                            <!-- ── Map Settings ── -->
                            ${this.showMapPage ? html`
                                <h4 class="section-header">Map Settings</h4>
                                <p class="info-paragraph">
                                    <b>Tip:</b> Pan and zoom the map with your trackpad or mouse.
                                    You can <span @click=${this.copyTextContent} style="font-weight:bold;text-decoration:underline;cursor:pointer">copy the text content</span> to your clipboard.
                                </p>
                                ${this.mapSettings.map((setting) => html`
                                    <setting-component
                                        .setting_details=${setting}
                                        @open-upgrade-dialog=${this.openUpgradeDialog}
                                        @on-change-value=${this.hasChange}
                                        @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.mapSettings)}
                                    ></setting-component>`)}
                            ` : ''}

                            <!-- ── Display Settings (shared) ── -->
                            ${this.showListOfPoints || this.showChecklist ? html`
                                <h4 class="section-header">Display Settings</h4>
                                ${this.displaySettings.map((setting) => html`
                                    <setting-component
                                        .setting_details=${setting}
                                        @open-upgrade-dialog=${this.openUpgradeDialog}
                                        @on-change-value=${this.hasChange}
                                    ></setting-component>`)}
                            ` : ''}

                            <!-- ── List of Points Settings ── -->
                            ${this.showListOfPoints ? html`
                                <h4 class="section-header">List of Points</h4>
                                ${this.listSettings.map((setting) => {
                                    const isCategorySort = this.pointOrdering[0] === PointOrderingOption.Category;
                                    const hideByCategorySort = (setting.fieldName === 'show_category_headers' && !isCategorySort)
                                        || (setting.fieldName === 'category_header_style' && (!isCategorySort || !this.showCategoryHeaders));
                                    return html`
                                    <setting-component
                                        style="${hideByCategorySort ? 'display:none;' : ''}"
                                        .setting_details=${setting}
                                        @open-upgrade-dialog=${this.openUpgradeDialog}
                                        @on-change-value=${this.hasChange}
                                    ></setting-component>`;})}
                            ` : ''}

                            <!-- ── Checklist / Bingo Settings ── -->
                            ${this.showChecklist ? html`
                                <h4 class="section-header">Checklist / Bingo</h4>
                                <setting-component
                                    .setting_details=${this.checklistStyleSetting}
                                    @on-change-value=${this.hasChange}
                                ></setting-component>
                                <setting-component
                                    .setting_details=${this.checklistSubtitleSetting}
                                    @on-change-value=${this.hasChange}
                                ></setting-component>
                                ${topicData.allChallenges.length > 1 ? html`
                                    <label class="checklist-select-label">
                                        Challenge
                                        <select @change=${this.onChallengeSelected}>
                                            ${topicData.allChallenges.map((c) => html`
                                                <option value=${c._id.$oid} ?selected=${c._id.$oid === this.selectedChallengeId}>${c.name}</option>
                                            `)}
                                        </select>
                                    </label>
                                ` : ''}
                            ` : ''}
                        </div>
                        <div>
                            <h4 class="section-header">Print Services</h4>
                            <p class="info-paragraph">
                                Proxi offers custom printing and map distribution services.
                            </p>
                            <form-button
                                id="printButton"
                                class="printButton"
                                size="md"
                                @click=${() => openChatWindow(this.amplitude_callsite, 'I am interested in printing services for my map.')}
                                >Click to Message Us
                            </form-button>
                        </div>
                    </div>
                    ` : ''
                }
            </div>
            <mwc-dialog id="upgradeDialogComponent" class="dialog" hideActions>
                <modal-dialog size="md" @close="${() => this.upgradeDialogComponent.close()}">
                    <upgrade-dialog
                        slot="body"
                        .featureType="${FeatureType.PrintableMap}"
                        @close="${() => this.upgradeDialogComponent.close()}"
                    >
                    </upgrade-dialog>
                </modal-dialog>
            </mwc-dialog>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    chunkArray<T>(array: T[], chunkSize: number): T[][] {
        const chunks: T[][] = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    }


private saveScrollPosition(): number {
    const printArea = this.shadowRoot?.querySelector('.printArea');
    return printArea?.scrollTop ?? 0;
}

private restoreScrollPosition(scrollTop: number) {
    const printArea = this.shadowRoot?.querySelector('.printArea');
    if (printArea) {
        printArea.scrollTop = scrollTop;
    }
}

private removePointListPages() {
    const pointPages = this.shadowRoot?.querySelectorAll('.rootContainer.point-list-page');
    pointPages?.forEach((el) => el.remove());
}

private rendermeasurePointsAndAdjustLayout(points: GetPointResponse[]) {
    const scrollTop = this.saveScrollPosition();
    // delete all existing point list pages
    this.removePointListPages();

    const printArea = this.shadowRoot?.querySelector('.printArea');
    if (!printArea) {
        return;
    }

    const createNewPage = () => {
        const newContainer = document.createElement('div');
        newContainer.classList.add('rootContainer', 'pagebreak', 'point-list-page');
        (newContainer as HTMLElement).style.height = `${this.pageHeight}${this.unit}`;
        (newContainer as HTMLElement).style.width = `${this.pageWidth}${this.unit}`;
        (newContainer as HTMLElement).style.display = 'flex';
        (newContainer as HTMLElement).style.flexDirection = 'row';

        const columns = [];
        for (let i = 0; i < this.numColumns; i++) {
            const columnContainer = document.createElement('div');
            columnContainer.id = 'points';
            newContainer.appendChild(columnContainer);
            columns.push(columnContainer);
        }

        return { newContainer, columns };
    };

    let { newContainer: currentContainer, columns } = createNewPage();
    printArea.appendChild(currentContainer);

    const pageHeightInPixels = currentContainer.clientHeight;
    const defaultColumnHeight = pageHeightInPixels - (6.5 * 16); // 3rem padding on top and bottom + some extra room
    let currentColumnIndex = 0;
    const numberOfColumns = this.numColumns;
    const remainingColumnHeights: number[] = [];
    for (let i = 0; i < numberOfColumns; i++) {
        remainingColumnHeights.push(defaultColumnHeight);
    }

    let lastCategoryId: string | undefined;

    const appendElement = (element: HTMLElement) => {
        columns[currentColumnIndex].appendChild(element);
        const elementHeight = element.clientHeight;

        if (elementHeight <= remainingColumnHeights[currentColumnIndex]) {
            remainingColumnHeights[currentColumnIndex] -= elementHeight;
        } else {
            currentColumnIndex++;
            if (currentColumnIndex >= this.numColumns) {
                ({ newContainer: currentContainer, columns } = createNewPage());
                printArea.appendChild(currentContainer);
                currentColumnIndex = 0;
                remainingColumnHeights.fill(defaultColumnHeight);
            }
            columns[currentColumnIndex].appendChild(element);
            remainingColumnHeights[currentColumnIndex] -= element.clientHeight;
        }
    };

    points.forEach((point, index) => {

        // Insert category header when category changes
        const isNewCategory = this.showCategoryHeaders && point.topic_category_id?.$oid !== lastCategoryId;
        if (isNewCategory) {
            lastCategoryId = point.topic_category_id?.$oid;
        }

        const pointElement = this.renderPointArea(point);
        const container = document.createElement('div');
        render(pointElement, container);

        if (isNewCategory) {
            const category = topicData.categories.find((c) => c._id?.$oid === point.topic_category_id.$oid);
            if (category) {
                const headerEl = document.createElement('div');
                const isBlock = this.categoryHeaderStyle === 'block';
                const bgColor = topicData.topic?.header_color ?? '#333333';
                const textColor = topicData.topic?.header_text_color ?? '#ffffff';
                headerEl.style.cssText = isBlock
                    ? `font-weight: bold; padding: calc(0.4rem * var(--text-scale, 1)) calc(0.3rem * var(--text-scale, 1)); font-size: calc(1.25em * var(--text-scale, 1)); margin-top: calc(0.5rem * var(--text-scale, 1)); margin-bottom: calc(0.2rem * var(--text-scale, 1)); background-color: ${bgColor}; color: ${textColor}; box-sizing: border-box; width: var(--column-width);`
                    : 'font-weight: bold; padding: calc(0.4rem * var(--text-scale, 1)) calc(0.2rem * var(--text-scale, 1)); font-size: calc(1.25em * var(--text-scale, 1)); margin-top: calc(0.5rem * var(--text-scale, 1)); margin-bottom: calc(0.2rem * var(--text-scale, 1));';
                headerEl.textContent = category.name;

                // Wrap header + first point together so header never sits alone at bottom of a column
                const wrapper = document.createElement('div');
                wrapper.appendChild(headerEl);
                wrapper.appendChild(container);
                appendElement(wrapper);
            } else {
                appendElement(container);
            }
        } else {
            appendElement(container);
        }
    });
    this.restoreScrollPosition(scrollTop);
}


    static get styles() {
        return css`

            :root {
            --page-width: 11in;
            --page-height: 8.5in;
            --page-scale: .6;
            --text-scale: 1;
            --column-width: 3.5in;
            --legend-background-color: #ffffff;
            --legend-position: bottom: 0; right: 0;
            --legend-text-color: #000000;
            }

            .point-info {
                max-width: calc(var(--column-width) - 2rem);
                display: flex;
                flex-direction: column;
                word-wrap: break-word;
                padding: calc(0.05rem * var(--text-scale)) calc(0.2rem * var(--text-scale));
            }

            .pageContainer{
                display: flex;
                flex-direction: row;
                height: 100vh;
                background-color: gray;
                height: calc(100vh - 2rem);
            }

            .printButton {
                display: flex;
            }

            .printButtonSticky {
                display: flex;
                width: 100%;
                flex-shrink: 0;
            }

            .legend-item {
                display: flex;
                align-items: center;
            }

            .legend-text {
                margin-left: 0.5rem;
            }

            .printArea {
                flex: 1;
                padding: 3rem;
                gap: 3rem;
                background-color: white;
                overflow-y: auto;
                overflow-x: hidden;
                display: grid;
                justify-content: center;
            }

            .header {
                margin-top: 1rem;
                margin-right: 30px;
                font-size: 1rem;
            }

            .section-header {
                margin: 1rem 0 0.25rem;
                padding-bottom: 0.25rem;
                font-size: 0.85rem;
                font-weight: 700;
                text-transform: uppercase;
                letter-spacing: 0.03em;
                color: #555;
                border-bottom: 1px solid #e0e0e0;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
            }

            .rootContainer {
                display: flex;
                flex-direction: column;
                height: var(--page-height);
                width: var(--page-width);
                box-shadow: 0 0 0.5in rgba(0, 0, 0, 0.5);
                background: white;
                transform: scale(var(--page-scale));
                padding: var(--page-padding, 0.5in);
            }

            #mainContainer {
                flex: 1;
            }

            .flexContainer {
                display: flex;
            }

            .textCenter {
                text-align: center;
            }

            #map {
                display: block;
                flex: 1;
                position: relative;
            }

            .qrCodeImg {
                height: 200px;
                position: absolute;
                left: 0;
                bottom: 0;
                z-index: 99;
            }

            .summary {
                width: 100%;
                display: inline-flex;
                align-items: center;
            }

            #summaryName {
                padding-left: 0.5rem;
                padding-right: 0.5rem;
                font-size: calc(1rem * var(--text-scale));
                font-weight: 700;
                margin-bottom: calc(0.05rem * var(--text-scale));
            }

            #categories {
                position: absolute;
                display: flex;
                padding: 1rem 1rem;
                background-color: var(--legend-background-color);
                color: var(--legend-text-color);
                z-index: 100;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 0px 10px;
            }

            #summaryAddress {
                padding-left: 0.5rem;
                font-size: calc(0.8rem * var(--text-scale));
                color: var(--theme-input-color);
                margin-bottom: calc(0.2rem * var(--text-scale))
            }

            #summaryDescription {
                padding-left: 0.5rem;
                font-size: calc(0.8rem * var(--text-scale));
                color: var(--theme-input-color);
                text-align: left;
                margin-bottom: calc(0.2rem * var(--text-scale))
            }

            #summaryDateTime {
                padding-left: 0.5rem;
                font-size: calc(0.8rem * var(--text-scale));
                color: var(--theme-input-color);
                font-style: italic;
                margin-bottom: calc(0.2rem * var(--text-scale))
            }

            #summaryOffer {
                padding-left: 0.5rem;
                font-size: calc(0.8rem * var(--text-scale));
                color: var(--theme-input-color);
                font-weight: bold;
            }

            #summaryTags {
                padding-left: 0.5rem;
                font-size: calc(0.8rem * var(--text-scale));
                color: var(--theme-input-color);
                line-height: 1.25rem;
            }

            #summaryMoreInfo {
                padding-left: 0.5rem;
                font-size: calc(0.8rem * var(--text-scale));
                color: var(--theme-input-color);
                font-weight: bold;
            }

            .tag {
                border: 0.25px solid var(--theme-input-color);
                border-radius: 25px;
                padding: 0.1rem 0.5rem;
                white-space: nowrap;
            }

            paper-chip {
                --paper-chip-background-color: transparent;
                --paper-chip-avatar-background-color: transparent;
            }

            .checklist-select-label {
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
                font-size: 0.875rem;
                margin-bottom: 0.5rem;
            }

            .checklist-select-label select {
                padding: 0.375rem 0.5rem;
                border: 1px solid #ccc;
                border-radius: 4px;
                font-size: 0.875rem;
            }

            .checklist-header {
                display: flex;
                align-items: center;
                gap: 0.2in;
                padding: 0.2in 0.3in;
                border-radius: 6px;
                margin-bottom: 0.2in;
            }

            .checklist-logo {
                max-width: 60px;
                max-height: 60px;
                object-fit: contain;
                flex-shrink: 0;
            }

            .checklist-qr {
                width: 120px;
                height: 120px;
                border-radius: 6px;
                background: white;
                padding: 4px;
            }

            .checklist-title-area {
                flex: 1;
                text-align: left;
            }

            .checklist-title {
                margin: 0;
                font-size: calc(1.4rem * var(--text-scale));
                font-weight: 700;
            }

            .checklist-subtitle {
                margin: 0.15rem 0 0;
                font-size: calc(0.85rem * var(--text-scale));
                opacity: 0.85;
            }

            .checklist-item {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                padding: calc(0.25rem * var(--text-scale)) 0;
                break-inside: avoid;
            }

            .checklist-checkbox {
                min-width: calc(18px * var(--text-scale));
                min-height: calc(18px * var(--text-scale));
                width: calc(18px * var(--text-scale));
                height: calc(18px * var(--text-scale));
                border: 2px solid #333;
                border-radius: 50%;
                flex-shrink: 0;
            }

            .checklist-item-content {
                display: flex;
                align-items: center;
                gap: 0.25rem;
            }

            .checklist-item-name {
                font-size: calc(0.9rem * var(--text-scale));
            }

            .bingo-grid {
                flex: 1;
                display: grid;
                gap: 0;
                border: 2px solid #333;
            }

            .bingo-cell {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                text-align: center;
                border: 1px solid #ccc;
                padding: 0.15in;
                gap: 4px;
                overflow: hidden;
            }

            .bingo-cell-name {
                font-size: calc(0.7rem * var(--text-scale));
                font-weight: 600;
                line-height: 1.2;
                word-break: break-word;
            }

            .bingo-free {
                background-color: #f0f0f0;
            }

            .bingo-free .bingo-cell-name {
                font-size: calc(0.9rem * var(--text-scale));
                font-weight: 700;
                opacity: 0.5;
            }

            #points {
                display: flex;
                flex-direction: column;
                flex-wrap: wrap;
                height: var(--page-height);
                align-items: flex-start;
                width: var(--column-width);
            }

            .pointRow {
                padding: 1rem;
            }

            .settings {
                width: 350px;
                min-width: 350px;
                background-color: white;
                padding: 1rem;
                height:calc(100% - 2rem);
                overflow: hidden;
                display: flex;
                flex-direction: column;
                gap: 5px;
            }

            @media print {
                .settings {
                    display: none;
                }

                .rootContainer {
                    box-shadow: none;
                    background: none;
                    margin-bottom: 0;
                    padding: 0;
                    transform: scale(1);
                    width: var(--page-width);
                    height: var(--page-height);
                }

                .printArea {
                    flex: 1;
                    padding: 0;
                    gap: 0;
                    overflow: visible;
                }

                .pagebreak { page-break-before: always; }

            }

            @page {
                size: var(--page-width) var(--page-height);
                margin: 0in;
                @top-right {
                    content: "Page " counter(pageNumber);
                }
            }

            @media screen and (max-width: 551px) {

                .pageContainer {
                    flex-direction: column;
                    height: auto;
                    background-color: white;
                }

                .settings {
                    height: auto;
                    overflow-x: hidden;
                    overflow-y: visible;
                }

                .printArea {
                    padding: 1rem;
                    overflow: visible;
                    transform: scale(0.4);
                    display: grid;
                    grid-template-columns: 1fr;
                }

                .rootContainer {
                    grid-column: 1;
                    grid-row: 1;
                }
        `;
    }
}
